// Simple IndexedDB-based draft store with TTL
import { api } from 'services/api';
import { bookingDraft } from './bookingDraft';
import { draftAccountSnapshot, draftAccountIsCurrent } from './draftAccountScope';
const DB_NAME = 'ds-platform';
const STORE = 'drafts';

function openDB() {
  return new Promise((resolve, reject) => {
    try {
      const req = window.indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = (e) => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: 'key' });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    } catch (e) {
      reject(e);
    }
  });
}

async function withStore(mode, fn) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const st = tx.objectStore(STORE);
    let value;
    const done = (v) => { value=v; };
    const fail = (err) => {
      try { db.close(); } catch {}
      reject(err);
    };
    tx.oncomplete = () => { db.close(); resolve(value); };
    tx.onerror = () => fail(tx.error);
    tx.onabort = () => fail(tx.error || new Error('draft_transaction_aborted'));
    fn(st, done, fail);
  });
}

export async function draftSet(key, data, ttlMs = 1000 * 60 * 60 * 48) {
  const account = draftAccountSnapshot();
  if (!draftAccountIsCurrent(account)) return false;
  const localKey = `${key}::${account.scope}`;
  let serverSaved = false;
  try {
    const updatedAt = Date.now();
    const expiresAt = updatedAt + Math.max(0, Number(ttlMs) || 0);
    const payload = { ...data, updatedAt };
    
    // server-first
    try {
      await api.drafts.save(payload);
      serverSaved = true;
    } catch { 
      /* fallback to local only if server fails */ 
    }
    if (!draftAccountIsCurrent(account)) return false;
    
    // always persist locally as redundancy and for reconciliation
    await withStore('readwrite', (st, done, fail) => {
      const req = st.put({ key: localKey, ownerScope: account.scope, data: payload, expiresAt, updatedAt });
      req.onsuccess = () => done(true);
      req.onerror = () => fail(req.error);
    });
    return draftAccountIsCurrent(account);
  } catch (err) {
    return draftAccountIsCurrent(account) && serverSaved;
  }
}

export async function draftGet(key) {
  const account = draftAccountSnapshot();
  if (!draftAccountIsCurrent(account)) return null;
  let serverDraft = null;
  let localRow = null;

  // 1. Fetch Server Draft
  try {
    const res = await api.drafts.get();
    serverDraft = bookingDraft(res?.draft?.data);
  } catch (e) {
    // console.log('[draftGet] Server fetch failed', e);
  }

  // 2. Fetch Local Draft
  try {
    localRow = await withStore('readonly', (st, done, fail) => {
      const req = st.get(`${key}::${account.scope}`);
      req.onsuccess = () => done(req.result || null);
      req.onerror = () => fail(req.error);
    });
  } catch (e) {
    // console.log('[draftGet] Local fetch failed', e);
  }
  if (!draftAccountIsCurrent(account)) return null;
  // Unbound historical rows remain stored, but cannot prove a private owner.
  if (localRow?.ownerScope !== account.scope) localRow = null;

  // 3. Reconcile
  if (!serverDraft && !localRow) return null;

  // If we only have one, check expiry for local
  if (!serverDraft && localRow) {
    if (localRow.expiresAt < Date.now()) return null;
    return localRow.data;
  }
  if (serverDraft && !localRow) return serverDraft;

  // If we have both, compare timestamps
  const serverTime = serverDraft?.updatedAt || 0;
  const localTime = localRow?.updatedAt || localRow?.data?.updatedAt || 0;

  if (serverTime >= localTime) {
    // JSON cannot carry a File. Recover only the matching attachment of the same
    // server-selected submission; never merge fields or another draft's photos.
    if (localRow.expiresAt >= Date.now() && serverDraft.submissionId &&
        serverDraft.submissionId === localRow.data?.submissionId) {
      const localImages = localRow.data?.taskData?.images || [];
      const images = serverDraft.taskData?.images;
      if (Array.isArray(images)) return {
        ...serverDraft,
        taskData: {...serverDraft.taskData, images: images.map(image => {
          const saved = localImages.find(candidate => candidate.id === image.id && candidate.name === image.name);
          return saved?.file instanceof Blob ? {...image, file: saved.file} : image;
        })},
      };
    }
    return serverDraft;
  } else {
    // Local is newer (likely guest progress before login context fully settled)
    // NOTE: We return local, but next save will sync it to server
    return localRow.data;
  }
}

export async function draftRemove(key) {
  const account = draftAccountSnapshot();
  if (!draftAccountIsCurrent(account)) return false;
  const localKey = `${key}::${account.scope}`;
  try {
    await api.drafts.clear();
    if (!draftAccountIsCurrent(account)) return false;
    // also clear local
    await withStore('readwrite', (st, done, fail) => {
      const req = st.delete(localKey);
      req.onsuccess = () => done(true);
      req.onerror = () => fail(req.error);
    });
    return true;
  } catch {
    if (!draftAccountIsCurrent(account)) return false;
    return withStore('readwrite', (st, done, fail) => {
      const req = st.delete(localKey);
      req.onsuccess = () => done(true);
      req.onerror = () => fail(req.error);
    });
  }
}

// An explicit successful login may claim an anonymous local-only draft once.
// Never move an owned or copied task between accounts, or guess a legacy owner.
export async function claimGuestDraft(previous) {
  const account = draftAccountSnapshot();
  if (previous?.scope !== 'guest' || !draftAccountIsCurrent(account) || !account.scope.endsWith(':customer')) return false;
  try {
    return await withStore('readwrite', (st, done, fail) => {
      const read = st.get('taskCreation::guest');
      read.onerror = () => fail(read.error);
      read.onsuccess = () => {
        const row = read.result;
        if (!draftAccountIsCurrent(account) || row?.ownerScope !== 'guest' || row.expiresAt < Date.now() ||
            row.data?.copiedFromTaskId || row.data?.taskData?.images?.some(image => image.sourceProof)) return done(false);
        const write = st.put({ ...row, key: `taskCreation::${account.scope}`, ownerScope: account.scope });
        write.onerror = () => fail(write.error);
        write.onsuccess = () => { st.delete('taskCreation::guest'); done(true); };
      };
    });
  } catch { return false; }
}

export async function draftClearExpired() {
  return withStore('readwrite', (st, done, _fail) => {
    const now = Date.now();
    const cursorReq = st.openCursor();
    cursorReq.onsuccess = (ev) => {
      const cursor = ev.target.result;
      if (!cursor) return done(true);
      const val = cursor.value;
      if (val && typeof val.expiresAt === 'number' && val.expiresAt < now) {
        try { st.delete(cursor.key); } catch {}
      }
      cursor.continue();
    };
  });
}
